import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  Input,
  Collapse,
  Modal,
  Popconfirm,
  Progress,
  Row,
  Select,
  Space,
  Steps,
  Switch,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from 'antd'
import {
  CopyOutlined,
  FolderOpenOutlined,
  PlusOutlined,
  ThunderboltOutlined,
  FileTextOutlined,
  UploadOutlined,
} from '@ant-design/icons'
import { adminApi, adminLongRunningApi, formatApiError, PAGE_SIZE, type PageResult } from '../../api'
import { AppFormModal, PageHeader, tablePagination, formatDate } from '../../components/ui'

const COPY_NAME_SUFFIX = '-副本'

const API_DOC_ACCEPT = '.md,.txt,.json,.pdf,.doc,.docx,text/markdown,text/plain,application/json'
/** 与后端 ApiDocItemIn.text max_length 一致 */
const API_DOC_TEXT_MAX = 50000

async function readApiDocFileContent(
  file: File,
): Promise<{ text: string; truncated: boolean }> {
  const fd = new FormData()
  fd.append('file', file)
  const r = await adminLongRunningApi.post<{ text: string; truncated: boolean }>(
    '/admin/ingestion/extract-api-doc-text',
    fd,
  )
  return { text: r.data.text || '', truncated: Boolean(r.data.truncated) }
}

function suggestCopySourceName(name: string): string {
  const base = (name || '采集源').trim()
  const maxBase = Math.max(1, 64 - COPY_NAME_SUFFIX.length)
  return `${base.slice(0, maxBase)}${COPY_NAME_SUFFIX}`
}

type SourceRow = {
  id: number
  name: string
  source_type: string
  description: string
  config_json: Record<string, unknown>
  is_active: boolean
  task_count: number
}

type TaskRow = {
  id: number
  name: string
  source_id: number
  source_name: string
  scope: string
  rule_id: number | null
  dimension_id: number | null
  dimension_name: string
  schedule_json: ScheduleJson
  is_enabled: boolean
  next_run_at: string | null
  last_run_at: string | null
}

type ScheduleJson = {
  type: 'none' | 'daily' | 'weekly' | 'interval'
  time?: string
  weekday?: number
  hours?: number
}

type RunRow = {
  id: number
  task_id: number
  task_name: string
  trigger_type: string
  status: string
  progress: number
  collect_progress: number
  process_progress: number
  status_message: string
  error_message: string
  total_files: number
  total_bytes: number
  collected_files: number
  collected_bytes: number
  processed_files: number
  processed_bytes: number
  skipped_files: number
  failed_files: number
  started_at: string | null
  finished_at: string | null
}

type FileJobRow = {
  id: number
  run_id: number | null
  task_id: number | null
  task_name?: string
  file_name: string
  status: string
  file_size_bytes: number
  category_path: string
  error_message: string
  item_id: number | null
}

type DimensionOption = {
  id: number
  name: string
  is_manual: boolean
  rule_id: number | null
}

const SOURCE_TYPE_LABEL: Record<string, string> = {
  nas: 'NAS / 网络目录',
  upload: '本地文件夹',
  ftp: 'FTP（即将支持）',
  api: 'API 接口',
}

type ApiDocItem = {
  filename: string
  text: string
  content_hash: string
}

type ApiDocRow = ApiDocItem & { status: 'pending' | 'analyzed' }

function initApiDocListFromConfig(cfg: Record<string, unknown>): ApiDocRow[] {
  const docs = (cfg.api_docs as Array<{ filename?: string; content_hash?: string }>) || []
  const analyzed = Boolean(cfg.api_analysis)
  return docs
    .map((d) => ({
      filename: String(d.filename || 'doc'),
      content_hash: String(d.content_hash || ''),
      text: '',
      status: analyzed ? ('analyzed' as const) : ('pending' as const),
    }))
    .filter((d) => d.content_hash)
}

function mergeApiDocRowsWithDisk(
  cfg: Record<string, unknown>,
  diskDocs: ApiDocItem[],
  localRows: ApiDocRow[],
): ApiDocRow[] {
  const analyzed = Boolean(cfg.api_analysis)
  const byHash = new Map(diskDocs.map((d) => [d.content_hash, d]))
  const byName = new Map(diskDocs.map((d) => [d.filename, d]))
  const out: ApiDocRow[] = []
  const seen = new Set<string>()

  for (const row of localRows) {
    const key = row.content_hash || row.filename
    if (!key || seen.has(key)) continue
    seen.add(key)
    if (row.text.trim()) {
      out.push(row)
      continue
    }
    const disk = byHash.get(row.content_hash) || byName.get(row.filename)
    if (disk?.text?.trim()) {
      out.push({
        filename: disk.filename,
        content_hash: disk.content_hash,
        text: disk.text,
        status: row.status === 'pending' ? 'pending' : analyzed ? 'analyzed' : 'pending',
      })
    } else {
      out.push(row)
    }
  }
  return out
}

async function fetchApiDocRowsFromServer(
  sourceId: number,
  cfg: Record<string, unknown>,
  localRows: ApiDocRow[],
): Promise<ApiDocRow[]> {
  const r = await adminApi.get<{ docs: ApiDocItem[] }>(`/admin/ingestion/sources/${sourceId}/api-docs`)
  return mergeApiDocRowsWithDisk(cfg, r.data.docs || [], localRows)
}

type ApiDocCheck = {
  key: string
  label: string
  found: boolean
  detail: string
}

type ApiPipelineParamBinding = {
  param?: string
  from_output?: string
  location?: string
  required?: boolean
}

type ApiPipelineOutputField = {
  save_as?: string
  fields?: string[]
  path?: string
  type?: string
}

type ApiIngestionPipelineStep = {
  step: number
  role?: string
  name: string
  url?: string
  method?: string
  required?: boolean
  depends_on?: string
  purpose?: string
  inputs?: ApiPipelineParamBinding[]
  outputs?: ApiPipelineOutputField[]
  request_params?: string[]
  response_fields?: string[]
}

type ApiMinimumCapability = {
  role: string
  label: string
  satisfied: boolean
  note: string
}

type ApiManualField = {
  key: string
  label: string
  param_name: string
  required: boolean
  secret: boolean
  hint: string
  value: string
  section: string
}

type ApiPermissionMapRow = { external: string; internal: string }

type ApiPermissionMapping = {
  enabled: boolean
  name_first?: boolean
  items_path: string
  fields: {
    subject_type: string
    subject_name: string
    subject_id: string
    permission_name: string
  }
  permission_name_map: ApiPermissionMapRow[]
  subject_type_map: ApiPermissionMapRow[]
}

const DEFAULT_PERMISSION_NAME_MAP: ApiPermissionMapRow[] = [
  { external: '查看', internal: 'view' },
  { external: '只读', internal: 'view' },
  { external: 'read', internal: 'view' },
  { external: 'view', internal: 'view' },
  { external: '编辑', internal: 'edit' },
  { external: 'edit', internal: 'edit' },
  { external: '完全控制', internal: 'full' },
  { external: 'full', internal: 'full' },
]

const DEFAULT_SUBJECT_TYPE_MAP: ApiPermissionMapRow[] = [
  { external: '用户', internal: 'user' },
  { external: '人员', internal: 'user' },
  { external: '部门', internal: 'org_unit' },
  { external: '角色', internal: 'role' },
  { external: '所有人', internal: 'everyone' },
]

const DEFAULT_PERMISSION_MAPPING: ApiPermissionMapping = {
  enabled: true,
  name_first: true,
  items_path: 'permissions',
  fields: {
    subject_type: 'subjectType',
    subject_name: 'subjectName',
    subject_id: 'subjectId',
    permission_name: 'permissionName',
  },
  permission_name_map: DEFAULT_PERMISSION_NAME_MAP,
  subject_type_map: DEFAULT_SUBJECT_TYPE_MAP,
}

function normalizePermissionMapping(raw: unknown): ApiPermissionMapping {
  const src = raw && typeof raw === 'object' ? (raw as Partial<ApiPermissionMapping>) : {}
  const fields =
    src.fields && typeof src.fields === 'object'
      ? (src.fields as Partial<ApiPermissionMapping['fields']>)
      : {}
  const permRows = Array.isArray(src.permission_name_map)
    ? src.permission_name_map
        .filter((r) => r && typeof r === 'object' && String(r.external || '').trim())
        .map((r) => ({
          external: String(r.external || '').trim(),
          internal: String(r.internal || '').trim(),
        }))
    : DEFAULT_PERMISSION_NAME_MAP
  const typeRows = Array.isArray(src.subject_type_map)
    ? src.subject_type_map
        .filter((r) => r && typeof r === 'object' && String(r.external || '').trim())
        .map((r) => ({
          external: String(r.external || '').trim(),
          internal: String(r.internal || '').trim(),
        }))
    : DEFAULT_SUBJECT_TYPE_MAP
  return {
    enabled: Boolean(src.enabled),
    name_first: src.name_first !== false,
    items_path: String(src.items_path || DEFAULT_PERMISSION_MAPPING.items_path).trim() || 'permissions',
    fields: {
      subject_type: String(fields.subject_type || DEFAULT_PERMISSION_MAPPING.fields.subject_type).trim(),
      subject_name: String(fields.subject_name || DEFAULT_PERMISSION_MAPPING.fields.subject_name).trim(),
      subject_id: String(fields.subject_id || DEFAULT_PERMISSION_MAPPING.fields.subject_id).trim(),
      permission_name: String(fields.permission_name || DEFAULT_PERMISSION_MAPPING.fields.permission_name).trim(),
    },
    permission_name_map: permRows.length ? permRows : DEFAULT_PERMISSION_NAME_MAP,
    subject_type_map: typeRows.length ? typeRows : DEFAULT_SUBJECT_TYPE_MAP,
  }
}

type ApiAnalysisResult = {
  summary: string
  vendor_name: string
  parse_source: string
  delivery_mode: string
  checks: ApiDocCheck[]
  missing_items: string[]
  api_simple: Record<string, unknown>
  api_docs: Array<{ filename: string; content_hash: string; uploaded_at?: string }>
  ingestion_pipeline?: ApiIngestionPipelineStep[]
  detected_endpoints?: ApiIngestionPipelineStep[]
  minimum_capabilities?: {
    can_collect: boolean
    minimum_steps: ApiMinimumCapability[]
    recommended_steps: ApiMinimumCapability[]
    gaps: string[]
  }
  permission_mapping_note?: string
  manual_fields?: ApiManualField[]
}

type ApiConnectStep = {
  key: string
  label: string
  status: 'ok' | 'fail' | 'skip'
  message: string
}

type ApiConnectCallLog = {
  index: number
  label: string
  phase: string
  method: string
  url: string
  request_headers?: Record<string, unknown>
  request_query?: Record<string, unknown>
  request_body?: unknown
  response_status?: number | null
  response_body?: string
  ok?: boolean
  error?: string | null
  elapsed_ms?: number | null
}

type ApiConnectCallGraph = {
  nodes?: Array<{
    step: number
    role?: string
    label: string
    method?: string
    url?: string
    required?: boolean
    called?: boolean
    status?: 'ok' | 'fail' | 'skip' | string
    reason?: string
    missing_inputs?: string[]
    actual_outputs?: Record<string, unknown>
    input_bindings?: Array<{
      param?: string
      from_output?: string
      source_step?: number | null
      source_label?: string
      available?: boolean
      missing?: boolean
    }>
    request?: {
      method?: string
      url?: string
      query?: Record<string, unknown>
      body?: unknown
    }
  }>
  edges?: Array<{
    from_step?: number | null
    from_label?: string
    to_step?: number
    to_label?: string
    param?: string
    from_output?: string
    matched?: boolean
    rule?: string
  }>
  rules?: string[]
  chain_trace?: string
}

type ApiConnectResult = {
  ok: boolean
  message: string
  file_count: number
  preview: { file_id: string; name: string; size_bytes: number }[]
  steps?: ApiConnectStep[]
  missing_items?: string[]
  call_logs?: ApiConnectCallLog[]
  call_graph?: ApiConnectCallGraph
}

type ApiCallOrderStep = {
  step: number
  name: string
  purpose: string
  depends_on?: string
}

type ApiParamIssue = {
  field: string
  current?: string
  expected_from_doc?: string
  fix?: string
}

type ApiProbeReviewReport = {
  summary: string
  call_order: ApiCallOrderStep[]
  param_issues: ApiParamIssue[]
  missing_items: string[]
  api_simple: Record<string, unknown>
  delivery_mode: string
  probe_ok: boolean
}

type AnalyzeProgressStep = {
  key: string
  title: string
  description?: string
  status: 'wait' | 'process' | 'finish' | 'error'
}

function applyManualFieldValuesToSimple(
  apiSimple: Record<string, unknown>,
  values: Record<string, string>,
): Record<string, unknown> {
  const out = { ...apiSimple }
  const runtime = { ...((out.runtime_params as Record<string, unknown>) || {}) }
  const listQuery = { ...((out.list_query as Record<string, unknown>) || {}) }
  const downloadQuery = { ...((out.download_query as Record<string, unknown>) || {}) }
  const steps = Array.isArray(out.auth_token_steps)
    ? (out.auth_token_steps as Record<string, unknown>[]).map((s) => ({ ...s }))
    : []

  const replaceDemoHost = (url: string, base: string) => {
    if (!url || !base) return url
    return url.replace(/^https?:\/\/[^/]+/i, base.replace(/\/$/, ''))
  }

  for (const [rawKey, rawVal] of Object.entries(values)) {
    const val = String(rawVal || '').trim()
    if (!val) continue
    if (rawKey.startsWith('runtime.')) {
      const pname = rawKey.slice('runtime.'.length)
      runtime[pname] = val
      listQuery[pname] = val
      downloadQuery[pname] = val
      out.service_user_param = pname
    } else if (rawKey.startsWith('oauth.')) {
      const pname = rawKey.slice('oauth.'.length)
      for (const step of steps) {
        for (const container of ['query', 'body'] as const) {
          const bag = step[container] as Record<string, unknown> | undefined
          if (!bag || typeof bag !== 'object') continue
          for (const fk of Object.keys(bag)) {
            if (fk.toLowerCase() === pname.toLowerCase()) {
              bag[fk] = val
            }
          }
        }
      }
    } else if (rawKey === 'api.base_url') {
      const base = val.replace(/\/$/, '')
      for (const urlKey of ['list_url', 'download_url_template', 'auth_token_url'] as const) {
        const url = String(out[urlKey] || '')
        if (/yunteams|example|xxx/i.test(url)) {
          out[urlKey] = replaceDemoHost(url, base)
        }
      }
      for (const step of steps) {
        const url = String(step.url || '')
        if (/yunteams|example|xxx/i.test(url)) {
          step.url = replaceDemoHost(url, base)
        }
      }
    }
  }

  out.runtime_params = runtime
  out.list_query = listQuery
  out.download_query = downloadQuery
  out.auth_token_steps = steps
  return out
}

function applyManualFieldsToAnalysis(
  analysis: ApiAnalysisResult,
  values: Record<string, string>,
): ApiAnalysisResult {
  return {
    ...analysis,
    api_simple: applyManualFieldValuesToSimple(analysis.api_simple || {}, values),
  }
}

function manualFieldsNeedInput(fields: ApiManualField[]): boolean {
  return fields.some((f) => f.required || !String(f.value || '').trim())
}

function createAnalyzeProgressSteps(): AnalyzeProgressStep[] {
  return [
    { key: 'read', title: '读取文档', status: 'wait' },
    { key: 'parse', title: 'AI 解析接口文档', status: 'wait' },
    { key: 'build', title: '组装接口配置', status: 'wait' },
    { key: 'probe', title: '调用接口探测', status: 'wait' },
    { key: 'review', title: 'AI 分析调用结果', status: 'wait' },
  ]
}

type ApiAnalysisStatePayload = {
  summary: string
  vendor_name: string
  parse_source: string
  delivery_mode: string
  checks: ApiDocCheck[]
  missing_items: string[]
  api_simple: Record<string, unknown>
  api_docs: Array<{ filename: string; content_hash: string; text?: string }>
  doc_count: number
  ingestion_pipeline?: ApiIngestionPipelineStep[]
  detected_endpoints?: ApiIngestionPipelineStep[]
  minimum_capabilities?: ApiAnalysisResult['minimum_capabilities']
  permission_mapping_note?: string
}

const API_PROBE_PHASE_LABEL: Record<string, string> = {
  auth_token: 'Token 鉴权',
  file_list: '文件列表',
  file_download: '文件下载',
}

async function sha256Hex(text: string): Promise<string> {
  const data = new TextEncoder().encode(text)
  const digest = await crypto.subtle.digest('SHA-256', data)
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

const INGESTION_ALLOWED_EXT = new Set([
  '.pdf',
  '.doc',
  '.docx',
  '.txt',
  '.md',
  '.markdown',
  '.png',
  '.jpg',
  '.jpeg',
  '.webp',
])

const UPLOAD_BATCH_SIZE = 20

function isRunnableSource(sourceType: string) {
  return sourceType === 'nas' || sourceType === 'upload' || sourceType === 'api'
}

function filterIngestionFiles(fileList: FileList | null): File[] {
  if (!fileList) return []
  const out: File[] = []
  for (const file of Array.from(fileList)) {
    const rel = file.webkitRelativePath || file.name
    const dot = rel.lastIndexOf('.')
    const ext = dot >= 0 ? rel.slice(dot).toLowerCase() : ''
    if (INGESTION_ALLOWED_EXT.has(ext)) out.push(file)
  }
  return out
}

async function uploadFolderToSource(sourceId: number, files: File[]) {
  for (let offset = 0; offset < files.length; offset += UPLOAD_BATCH_SIZE) {
    const batch = files.slice(offset, offset + UPLOAD_BATCH_SIZE)
    const fd = new FormData()
    const paths: string[] = []
    for (const file of batch) {
      fd.append('files', file, file.name)
      paths.push(file.webkitRelativePath || file.name)
    }
    fd.append('relative_paths', JSON.stringify(paths))
    fd.append('replace', offset === 0 ? 'true' : 'false')
    await adminApi.post(`/admin/ingestion/sources/${sourceId}/upload-folder`, fd)
  }
}

const SCOPE_LABEL: Record<string, string> = {
  enterprise: '企业知识库',
  personal: '个人知识库',
}

const SCOPE_OPTIONS = (['enterprise', 'personal'] as const).map((value) => ({
  value,
  label: SCOPE_LABEL[value],
}))

function runTriggerLabel(trigger: string) {
  return trigger === 'schedule' ? '定时' : '手动'
}

function formatRunOptionLabel(run: RunRow) {
  const when = formatDate(run.started_at || run.finished_at) || '未开始'
  const task = run.task_name?.trim() || `任务 #${run.task_id}`
  return `${when} · ${task} · ${runTriggerLabel(run.trigger_type)}`
}

const WEEKDAYS = [
  { value: 1, label: '周一' },
  { value: 2, label: '周二' },
  { value: 3, label: '周三' },
  { value: 4, label: '周四' },
  { value: 5, label: '周五' },
  { value: 6, label: '周六' },
  { value: 7, label: '周日' },
]

const RUN_ACTIVE = new Set(['pending', 'scanning', 'collecting', 'processing'])

function formatBytes(n: number) {
  if (!n) return '0 B'
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`
}

function scheduleSummary(s: ScheduleJson) {
  if (!s || s.type === 'none') return '不定时'
  if (s.type === 'daily') return `每天 ${s.time || '02:00'}`
  if (s.type === 'weekly') {
    const wd = WEEKDAYS.find((w) => w.value === s.weekday)?.label || '周一'
    return `每${wd} ${s.time || '02:00'}`
  }
  if (s.type === 'interval') return `每 ${s.hours || 6} 小时`
  return '不定时'
}

function runStatusTag(status: string) {
  const map: Record<string, string> = {
    pending: 'default',
    scanning: 'processing',
    collecting: 'processing',
    processing: 'processing',
    done: 'success',
    failed: 'error',
  }
  const label: Record<string, string> = {
    pending: '排队',
    scanning: '扫描中',
    collecting: '采集中',
    processing: '解析入库',
    done: '完成',
    failed: '失败',
  }
  return <Tag color={map[status] || 'default'}>{label[status] || status}</Tag>
}

function jobStatusTag(status: string) {
  const map: Record<string, string> = {
    staged: 'default',
    pending: 'default',
    processing: 'processing',
    done: 'success',
    failed: 'error',
    skipped: 'warning',
  }
  const label: Record<string, string> = {
    staged: '已采集',
    pending: '待处理',
    processing: '解析中',
    done: '完成',
    failed: '失败',
    skipped: '跳过',
  }
  return <Tag color={map[status] || 'default'}>{label[status] || status}</Tag>
}

export default function IngestionManage() {
  const [tab, setTab] = useState('sources')
  const [sources, setSources] = useState<SourceRow[]>([])
  const [sourceTotal, setSourceTotal] = useState(0)
  const [sourcePage, setSourcePage] = useState(1)
  const [sourceLoading, setSourceLoading] = useState(false)
  const [sourceModalOpen, setSourceModalOpen] = useState(false)
  const [editingSource, setEditingSource] = useState<SourceRow | null>(null)
  const [sourceForm] = Form.useForm()
  const [copySourceOpen, setCopySourceOpen] = useState(false)
  const [copySourceTarget, setCopySourceTarget] = useState<SourceRow | null>(null)
  const [copySourceName, setCopySourceName] = useState('')
  const [copySourceSubmitting, setCopySourceSubmitting] = useState(false)
  const [sourceSaving, setSourceSaving] = useState(false)
  const [pendingUploadFiles, setPendingUploadFiles] = useState<File[]>([])
  const [apiDocList, setApiDocList] = useState<ApiDocRow[]>([])
  const [apiAnalysis, setApiAnalysis] = useState<ApiAnalysisResult | null>(null)
  const [permissionMappingDraft, setPermissionMappingDraft] = useState<ApiPermissionMapping>(
    DEFAULT_PERMISSION_MAPPING,
  )
  const [apiDocParsing, setApiDocParsing] = useState(false)
  /** 全流程进行中（含探测/审查）；Steps 与防重复点击用，不等同于按钮 loading */
  const [apiAnalyzeFlowActive, setApiAnalyzeFlowActive] = useState(false)
  const [apiAnalyzeSteps, setApiAnalyzeSteps] = useState<AnalyzeProgressStep[]>([])
  const [apiProbeReview, setApiProbeReview] = useState<ApiProbeReviewReport | null>(null)
  const [apiConnectResult, setApiConnectResult] = useState<ApiConnectResult | null>(null)
  const [apiConnectLogOpen, setApiConnectLogOpen] = useState(false)
  const [apiManualFieldsOpen, setApiManualFieldsOpen] = useState(false)
  const [apiManualFieldsDraft, setApiManualFieldsDraft] = useState<ApiManualField[]>([])
  const [apiManualFieldValues, setApiManualFieldValues] = useState<Record<string, string>>({})
  const apiManualFieldsResolveRef = useRef<((values: Record<string, string> | null) => void) | null>(null)
  const folderInputRef = useRef<HTMLInputElement>(null)
  const apiDocInputRef = useRef<HTMLInputElement>(null)

  const sourceType = Form.useWatch('source_type', sourceForm) as string | undefined

  const [tasks, setTasks] = useState<TaskRow[]>([])
  const [taskTotal, setTaskTotal] = useState(0)
  const [taskPage, setTaskPage] = useState(1)
  const [taskLoading, setTaskLoading] = useState(false)
  const [taskModalOpen, setTaskModalOpen] = useState(false)
  const [editingTask, setEditingTask] = useState<TaskRow | null>(null)
  const [taskForm] = Form.useForm()
  const [dimensions, setDimensions] = useState<DimensionOption[]>([])
  const [dimensionsLoading, setDimensionsLoading] = useState(false)
  const [runningTaskId, setRunningTaskId] = useState<number | null>(null)

  const [runs, setRuns] = useState<RunRow[]>([])
  const [runTotal, setRunTotal] = useState(0)
  const [runPage, setRunPage] = useState(1)
  const [runLoading, setRunLoading] = useState(false)
  const [activeRun, setActiveRun] = useState<RunRow | null>(null)
  const [filterRunTaskId, setFilterRunTaskId] = useState<number | undefined>()

  const [runDetailRun, setRunDetailRun] = useState<RunRow | null>(null)
  const [runDetailJobs, setRunDetailJobs] = useState<FileJobRow[]>([])
  const [runDetailJobTotal, setRunDetailJobTotal] = useState(0)
  const [runDetailJobPage, setRunDetailJobPage] = useState(1)
  const [runDetailJobLoading, setRunDetailJobLoading] = useState(false)

  const sourceOptions = useMemo(
    () => sources.filter((s) => s.is_active).map((s) => ({ value: s.id, label: s.name })),
    [sources],
  )

  const loadSources = useCallback(() => {
    setSourceLoading(true)
    return adminApi
      .get<PageResult<SourceRow>>('/admin/ingestion/sources', {
        params: { page: sourcePage, page_size: PAGE_SIZE },
      })
      .then((r) => {
        setSources(r.data.items || [])
        setSourceTotal(r.data.total || 0)
      })
      .catch((e) => message.error(formatApiError(e, '加载采集源失败')))
      .finally(() => setSourceLoading(false))
  }, [sourcePage])

  const loadTasks = useCallback(() => {
    setTaskLoading(true)
    return adminApi
      .get<PageResult<TaskRow>>('/admin/ingestion/tasks', {
        params: { page: taskPage, page_size: PAGE_SIZE },
      })
      .then((r) => {
        setTasks(r.data.items || [])
        setTaskTotal(r.data.total || 0)
      })
      .catch((e) => message.error(formatApiError(e, '加载采集任务失败')))
      .finally(() => setTaskLoading(false))
  }, [taskPage])

  const taskOptions = useMemo(
    () => tasks.map((t) => ({ value: t.id, label: t.name })),
    [tasks],
  )

  const loadRuns = useCallback(() => {
    setRunLoading(true)
    return adminApi
      .get<PageResult<RunRow>>('/admin/ingestion/runs', {
        params: {
          page: runPage,
          page_size: PAGE_SIZE,
          ...(filterRunTaskId != null ? { task_id: filterRunTaskId } : {}),
        },
      })
      .then((r) => {
        setRuns(r.data.items || [])
        setRunTotal(r.data.total || 0)
        const active = (r.data.items || []).find((x) => RUN_ACTIVE.has(x.status))
        if (active) setActiveRun(active)
      })
      .catch((e) => message.error(formatApiError(e, '加载运行记录失败')))
      .finally(() => setRunLoading(false))
  }, [runPage, filterRunTaskId])

  const loadRunDetailJobs = useCallback(() => {
    if (runDetailRun == null) {
      setRunDetailJobs([])
      setRunDetailJobTotal(0)
      setRunDetailJobLoading(false)
      return Promise.resolve()
    }
    setRunDetailJobLoading(true)
    return adminApi
      .get<PageResult<FileJobRow>>('/admin/ingestion/jobs', {
        params: {
          page: runDetailJobPage,
          page_size: PAGE_SIZE,
          run_id: runDetailRun.id,
        },
      })
      .then((r) => {
        setRunDetailJobs(r.data.items || [])
        setRunDetailJobTotal(r.data.total || 0)
      })
      .catch((e) => message.error(formatApiError(e, '加载文件明细失败')))
      .finally(() => setRunDetailJobLoading(false))
  }, [runDetailRun, runDetailJobPage])

  useEffect(() => {
    adminApi
      .get<PageResult<SourceRow>>('/admin/ingestion/sources', { params: { page: 1, page_size: 100 } })
      .then((r) => setSources(r.data.items || []))
      .catch(() => {})
    adminApi
      .get<PageResult<TaskRow>>('/admin/ingestion/tasks', { params: { page: 1, page_size: 100 } })
      .then((r) => setTasks(r.data.items || []))
      .catch(() => {})
  }, [])

  useEffect(() => {
    if (tab === 'sources') void loadSources()
  }, [tab, loadSources])

  useEffect(() => {
    if (tab === 'tasks') void loadTasks()
  }, [tab, loadTasks])

  useEffect(() => {
    if (tab === 'runs') void loadRuns()
  }, [tab, loadRuns])

  useEffect(() => {
    if (runDetailRun == null) return
    void loadRunDetailJobs()
  }, [runDetailRun, loadRunDetailJobs])

  useEffect(() => {
    if (!activeRun || !RUN_ACTIVE.has(activeRun.status)) return
    const timer = setInterval(() => {
      adminApi
        .get<RunRow>(`/admin/ingestion/runs/${activeRun.id}`)
        .then((r) => {
          setActiveRun(r.data)
          if (!RUN_ACTIVE.has(r.data.status)) {
            void loadRuns()
            void loadTasks()
            if (runDetailRun?.id === r.data.id) void loadRunDetailJobs()
          }
        })
        .catch(() => {})
    }, 2000)
    return () => clearInterval(timer)
  }, [activeRun, loadRuns, loadTasks, runDetailRun, loadRunDetailJobs])

  const resetSourceFormDefaults = useCallback(() => {
    sourceForm.setFieldsValue({
      name: '',
      source_type: 'upload',
      description: '',
      root_path: '',
      folder_name: '',
      include_subdirs: true,
      is_active: true,
    })
  }, [sourceForm])

  const resetSourceApiDraft = useCallback(() => {
    setPendingUploadFiles([])
    setApiDocList([])
    setApiAnalysis(null)
    setPermissionMappingDraft(DEFAULT_PERMISSION_MAPPING)
    setApiProbeReview(null)
    setApiConnectResult(null)
    setApiAnalyzeSteps([])
    setApiConnectLogOpen(false)
    setApiDocParsing(false)
    setApiAnalyzeFlowActive(false)
  }, [])

  const resetSourceModalDraft = useCallback(() => {
    setEditingSource(null)
    resetSourceApiDraft()
    resetSourceFormDefaults()
  }, [resetSourceApiDraft, resetSourceFormDefaults])

  const isNewSourceDraftDirty = useCallback((): boolean => {
    if (editingSource) return false
    const v = sourceForm.getFieldsValue()
    if ((v.name || '').trim()) return true
    if ((v.description || '').trim()) return true
    if (v.source_type && v.source_type !== 'upload') return true
    if ((v.root_path || '').trim()) return true
    if (pendingUploadFiles.length > 0) return true
    if (apiDocList.length > 0) return true
    if (apiAnalysis) return true
    if (apiProbeReview) return true
    if (apiConnectResult) return true
    if (apiAnalyzeSteps.length > 0) return true
    return false
  }, [
    editingSource,
    sourceForm,
    pendingUploadFiles.length,
    apiDocList.length,
    apiAnalysis,
    apiProbeReview,
    apiConnectResult,
    apiAnalyzeSteps.length,
  ])

  const openSourceCreate = () => {
    resetSourceModalDraft()
    setSourceModalOpen(true)
  }

  const openSourceEdit = (row: SourceRow) => {
    setEditingSource(row)
    setPendingUploadFiles([])
    const initialDocs = initApiDocListFromConfig(row.config_json || {})
    setApiDocList(initialDocs)
    if (row.source_type === 'api' && row.id) {
      void fetchApiDocRowsFromServer(row.id, row.config_json || {}, initialDocs)
        .then((hydrated) => {
          if (hydrated.length) setApiDocList(hydrated)
        })
        .catch(() => {})
    }
    const existingAnalysis = row.config_json?.api_analysis as ApiAnalysisResult | undefined
    setApiAnalysis(
      existingAnalysis && Array.isArray(existingAnalysis.checks)
        ? existingAnalysis
        : null,
    )
    const cfgSimple = row.config_json?.api_simple as Record<string, unknown> | undefined
    const analysisSimple = existingAnalysis?.api_simple
    setPermissionMappingDraft(
      normalizePermissionMapping(
        (analysisSimple as Record<string, unknown> | undefined)?.permission_mapping ??
          cfgSimple?.permission_mapping,
      ),
    )
    setApiProbeReview(null)
    setApiConnectResult(null)
    setApiAnalyzeSteps([])
    setApiConnectLogOpen(false)
    setApiDocParsing(false)
    setApiAnalyzeFlowActive(false)
    sourceForm.setFieldsValue({
      name: row.name,
      source_type: row.source_type,
      description: row.description,
      root_path: String(row.config_json?.root_path || ''),
      folder_name: String(row.config_json?.folder_name || ''),
      include_subdirs: row.config_json?.include_subdirs !== false,
      is_active: row.is_active,
    })
    setSourceModalOpen(true)
  }

  const resolveApiAnalysis = (): ApiAnalysisResult | null => {
    if (apiAnalysis) return apiAnalysis
    const raw = editingSource?.config_json?.api_analysis
    if (raw && typeof raw === 'object' && Array.isArray((raw as ApiAnalysisResult).checks)) {
      return raw as ApiAnalysisResult
    }
    return null
  }

  const runAnalyzeApiDocs = () => {
    if (!apiDocList.length) {
      message.warning('请先在上方上传接口文档')
      return
    }
    void analyzeApiDocs(apiDocList)
  }

  const removeApiDoc = async (row: ApiDocRow) => {
    const persisted = Boolean(
      editingSource?.id &&
        ((editingSource.config_json?.api_docs as ApiDocItem[]) || []).some(
          (d) => d.content_hash === row.content_hash,
        ),
    )
    if (persisted) {
      try {
        await adminApi.delete(`/admin/ingestion/sources/${editingSource!.id}/api-docs`, {
          params: { content_hash: row.content_hash },
        })
      } catch (e) {
        message.error(formatApiError(e, '删除失败'))
        return
      }
    }
    setApiDocList((prev) =>
      prev
        .filter((d) => d.content_hash !== row.content_hash)
        .map((d) => ({ ...d, status: 'pending' as const })),
    )
    setApiAnalysis(null)
    setApiProbeReview(null)
    setApiConnectResult(null)
    if (editingSource) {
      const nextCfg = { ...editingSource.config_json }
      nextCfg.api_docs = ((nextCfg.api_docs as ApiDocItem[]) || []).filter(
        (d) => d.content_hash !== row.content_hash,
      )
      delete nextCfg.api_analysis
      delete nextCfg.api_simple
      setEditingSource({ ...editingSource, config_json: nextCfg })
    }
    message.success('已删除，可重新上传并分析')
  }

  const patchAnalyzeStep = (key: string, patch: Partial<AnalyzeProgressStep>) => {
    setApiAnalyzeSteps((prev) => prev.map((s) => (s.key === key ? { ...s, ...patch } : s)))
  }

  const applyProbeReview = (
    probe: ApiConnectResult | null,
    review: ApiProbeReviewReport | null,
  ) => {
    setApiConnectResult(probe)
    setApiProbeReview(review)
    setApiConnectLogOpen(false)
    if (review?.api_simple && typeof review.api_simple === 'object') {
      setApiAnalysis((prev) =>
        prev
          ? {
              ...prev,
              api_simple: review.api_simple,
              missing_items: review.missing_items || prev.missing_items,
              delivery_mode: review.delivery_mode || prev.delivery_mode,
            }
          : prev,
      )
    }
  }

  const toAnalysisStatePayload = (
    data: ApiAnalysisResult & { doc_count?: number },
    docs: Array<{ filename: string; content_hash: string; text?: string }>,
  ): ApiAnalysisStatePayload => ({
    summary: data.summary || '',
    vendor_name: data.vendor_name || '',
    parse_source: data.parse_source || 'ai',
    delivery_mode: data.delivery_mode || 'download_url',
    checks: data.checks || [],
    missing_items: data.missing_items || [],
    api_simple: data.api_simple || {},
    api_docs: docs,
    doc_count: data.doc_count ?? docs.length,
    ingestion_pipeline: data.ingestion_pipeline,
    detected_endpoints: data.detected_endpoints,
    minimum_capabilities: data.minimum_capabilities,
    permission_mapping_note: data.permission_mapping_note,
  })

  const promptApiManualFields = (fields: ApiManualField[]) =>
    new Promise<Record<string, string> | null>((resolve) => {
      const initial: Record<string, string> = {}
      for (const f of fields) {
        initial[f.key] = String(f.value || '').trim()
      }
      setApiManualFieldValues(initial)
      setApiManualFieldsDraft(fields)
      apiManualFieldsResolveRef.current = resolve
      setApiManualFieldsOpen(true)
    })

  const closeApiManualFields = (values: Record<string, string> | null) => {
    apiManualFieldsResolveRef.current?.(values)
    apiManualFieldsResolveRef.current = null
    setApiManualFieldsOpen(false)
    setApiManualFieldsDraft([])
  }

  const confirmApiManualFields = () => {
    const missingRequired = apiManualFieldsDraft.filter(
      (f) => f.required && !String(apiManualFieldValues[f.key] || '').trim(),
    )
    if (missingRequired.length) {
      message.warning(`请填写：${missingRequired.map((f) => f.label).join('、')}`)
      return
    }
    closeApiManualFields(apiManualFieldValues)
  }

  const runProbeAndReview = async (initialState: ApiAnalysisStatePayload) => {
    patchAnalyzeStep('probe', { status: 'process', description: '按当前配置调用鉴权 / 列表 / 下载…' })
    const probeRes = await adminLongRunningApi.post<{ ok: boolean; connect_probe: ApiConnectResult }>(
      '/admin/ingestion/connect-probe',
      initialState,
    )
    const probe = probeRes.data.connect_probe
    setApiConnectResult(probe)
    const probeOk = Boolean(probeRes.data.ok)
    patchAnalyzeStep('probe', {
      status: probeOk ? 'finish' : 'error',
      description: probe?.message || (probeOk ? '探测通过' : '探测未完全通过'),
    })

    patchAnalyzeStep('review', { status: 'process', description: '对照文档与 HTTP 日志分析参数与调用顺序…' })
    const reviewRes = await adminLongRunningApi.post<{ ok: boolean; probe_review: ApiProbeReviewReport }>(
      '/admin/ingestion/connect-probe-review',
      { state: initialState, connect_probe: probe },
    )
    const review = reviewRes.data.probe_review
    applyProbeReview(probe, review)
    patchAnalyzeStep('review', {
      status: 'finish',
      description: review.summary || '审查完成',
    })
    return { probe, review, probeOk, state: { ...initialState, api_simple: review.api_simple, missing_items: review.missing_items, delivery_mode: review.delivery_mode } }
  }

  const analyzeApiDocs = async (docsInModal: ApiDocRow[]) => {
    setApiAnalyzeFlowActive(true)
    setApiDocParsing(true)
    setApiAnalysis(null)
    setApiAnalyzeSteps(createAnalyzeProgressSteps())
    setApiProbeReview(null)
    setApiConnectResult(null)
    setApiDocList((prev) => prev.map((d) => ({ ...d, status: 'pending' as const })))
    try {
      patchAnalyzeStep('read', { status: 'process', description: '加载文档正文…' })
      let docsToSend = docsInModal
      if (editingSource?.id) {
        docsToSend = await fetchApiDocRowsFromServer(
          editingSource.id,
          editingSource.config_json || {},
          docsInModal,
        )
        setApiDocList(docsToSend)
      }
      const missingBody = docsToSend.filter((d) => !d.text.trim())
      if (missingBody.length) {
        patchAnalyzeStep('read', { status: 'error', description: '部分文档缺少正文' })
        message.error(`「${missingBody[0].filename}」缺少正文，请从列表中删除后重新上传`)
        return
      }
      patchAnalyzeStep('read', {
        status: 'finish',
        description: `已读取 ${docsToSend.length} 份文档`,
      })

      patchAnalyzeStep('parse', { status: 'process', description: 'AI 正在解析列表 / 鉴权 / 字段…' })
      const r = await adminLongRunningApi.post<
        ApiAnalysisResult & {
          ok: boolean
          doc_count: number
        }
      >('/admin/ingestion/analyze-api-docs', {
        docs: docsToSend.map((d) => ({
          filename: d.filename,
          text: d.text,
          content_hash: d.content_hash,
        })),
        source_id: editingSource?.id ?? null,
      })
      const data = r.data
      const analyzedDocs = data.api_docs || []
      patchAnalyzeStep('parse', {
        status: 'finish',
        description: `已解析 ${data.doc_count || docsToSend.length} 份文档`,
      })

      patchAnalyzeStep('build', { status: 'process', description: '生成 api_simple / api_spec…' })
      const analysisResult: ApiAnalysisResult = {
        summary: data.summary || '',
        vendor_name: data.vendor_name || '',
        parse_source: data.parse_source || 'ai',
        delivery_mode: data.delivery_mode || 'download_url',
        checks: data.checks || [],
        missing_items: data.missing_items || [],
        api_simple: data.api_simple || {},
        api_docs: analyzedDocs,
        ingestion_pipeline: data.ingestion_pipeline || [],
        detected_endpoints: data.detected_endpoints || [],
        minimum_capabilities: data.minimum_capabilities,
        permission_mapping_note: data.permission_mapping_note || '',
        manual_fields: data.manual_fields || [],
      }
      let resolvedAnalysis = analysisResult
      const manualFields = data.manual_fields || []
      if (manualFieldsNeedInput(manualFields)) {
        patchAnalyzeStep('build', {
          status: 'finish',
          description: '等待补全探测参数…',
        })
        setApiDocParsing(false)
        const filled = await promptApiManualFields(manualFields)
        if (!filled) {
          message.info('已取消参数补全；可再次点击「AI 分析并测试连通」继续')
          return
        }
        resolvedAnalysis = applyManualFieldsToAnalysis(analysisResult, filled)
      }
      setApiAnalysis(resolvedAnalysis)
      setPermissionMappingDraft(
        normalizePermissionMapping(resolvedAnalysis.api_simple?.permission_mapping),
      )
      setApiDocList((prev) =>
        prev.map((row) => {
          const hit = analyzedDocs.find(
            (d) => d.content_hash === row.content_hash || d.filename === row.filename,
          )
          return {
            ...row,
            filename: hit?.filename || row.filename,
            content_hash: hit?.content_hash || row.content_hash,
            status: 'analyzed' as const,
          }
        }),
      )
      if (editingSource && analyzedDocs.length) {
        setEditingSource((prev) =>
          prev
            ? {
                ...prev,
                config_json: {
                  ...prev.config_json,
                  api_docs: analyzedDocs.map(({ filename, content_hash }) => ({ filename, content_hash })),
                  api_analysis: {
                    checks: data.checks,
                    missing_items: data.missing_items,
                    summary: data.summary,
                    vendor_name: data.vendor_name,
                    parse_source: data.parse_source,
                    ingestion_pipeline: data.ingestion_pipeline,
                    detected_endpoints: data.detected_endpoints,
                    minimum_capabilities: data.minimum_capabilities,
                    permission_mapping_note: data.permission_mapping_note,
                  },
                  api_simple: data.api_simple,
                },
              }
            : prev,
        )
      }
      if (!manualFieldsNeedInput(manualFields)) {
        patchAnalyzeStep('build', { status: 'finish', description: '接口配置已就绪' })
      }
      setApiDocParsing(false)

      const verifyOutcome = await runProbeAndReview(
        toAnalysisStatePayload({ ...resolvedAnalysis, doc_count: data.doc_count }, docsToSend),
      )
      if (editingSource && verifyOutcome.review.api_simple) {
        setEditingSource((prev) =>
          prev
            ? {
                ...prev,
                config_json: {
                  ...prev.config_json,
                  api_simple: verifyOutcome.review.api_simple,
                  api_analysis: {
                    ...(prev.config_json?.api_analysis as object),
                    missing_items: verifyOutcome.review.missing_items,
                  },
                },
              }
            : prev,
        )
      }

      const docCount = data.doc_count || docsInModal.length
      const issueCount =
        verifyOutcome.probeOk ? 0 : verifyOutcome.review.param_issues?.length || 0
      if (verifyOutcome.probeOk) {
        message.success(`已分析 ${docCount} 份文档，接口探测通过`)
      } else if (issueCount) {
        message.warning(`已分析 ${docCount} 份文档，探测未通过，发现 ${issueCount} 处参数问题`)
      } else {
        message.warning(`已分析 ${docCount} 份文档，接口探测未完全通过，请查看调用日志`)
      }
    } catch (e) {
      setApiAnalyzeSteps((prev) => {
        const active = prev.find((s) => s.status === 'process')
        if (!active) return prev
        return prev.map((s) =>
          s.key === active.key ? { ...s, status: 'error', description: formatApiError(e, '失败') } : s,
        )
      })
      message.error(formatApiError(e, '分析失败'))
    } finally {
      setApiDocParsing(false)
      setApiAnalyzeFlowActive(false)
    }
  }

  const handleApiDocFileChange = async (fileList: FileList | null) => {
    if (!fileList?.length) return
    const files = Array.from(fileList)
    const existingHashes = new Set(apiDocList.map((d) => d.content_hash).filter(Boolean))
    const newItems: ApiDocRow[] = []
    for (const file of files) {
      let parsed: { text: string; truncated: boolean }
      try {
        parsed = await readApiDocFileContent(file)
      } catch (e) {
        message.error(formatApiError(e, `${file.name} 读取失败`))
        continue
      }
      const { text, truncated } = parsed
      if (!text.trim()) {
        message.warning(`${file.name} 内容为空，已跳过`)
        continue
      }
      if (truncated) {
        message.warning(`${file.name} 超过 ${API_DOC_TEXT_MAX} 字，已截取前 ${API_DOC_TEXT_MAX} 字用于分析`)
      }
      const content_hash = await sha256Hex(text)
      if (existingHashes.has(content_hash)) {
        message.warning(`文档重复，请勿重复上传：${file.name}`)
        continue
      }
      existingHashes.add(content_hash)
      newItems.push({ filename: file.name, text, content_hash, status: 'pending' })
    }
    if (!newItems.length) return
    setApiDocList((prev) => [
      ...prev.map((d) => ({ ...d, status: 'pending' as const })),
      ...newItems,
    ])
    setApiAnalysis(null)
    setApiProbeReview(null)
    setApiConnectResult(null)
    message.success(`已添加 ${newItems.length} 份文档，请点击「AI 分析并测试连通」`)
  }

  const renderCheckTag = (found: boolean) => (
    <Tag color={found ? 'success' : 'warning'} style={{ marginInlineEnd: 0, flexShrink: 0 }}>
      {found ? '已匹配' : '未匹配'}
    </Tag>
  )

  const renderStepTag = (status: ApiConnectStep['status']) => {
    if (status === 'ok') return <Tag color="success">正常</Tag>
    if (status === 'fail') return <Tag color="error">异常</Tag>
    return <Tag>跳过</Tag>
  }

  const normalizeProbeStatus = (status?: string): ApiConnectStep['status'] => {
    if (status === 'ok' || status === 'fail') return status
    return 'skip'
  }

  const pickUploadFolder = () => {
    folderInputRef.current?.click()
  }

  const handleUploadFolderChange = (fileList: FileList | null) => {
    const files = filterIngestionFiles(fileList)
    if (!files.length) {
      message.warning('所选文件夹中没有支持的文档格式')
      return
    }
    setPendingUploadFiles(files)
    const folder = files[0]?.webkitRelativePath?.split('/')[0] || ''
    sourceForm.setFieldValue('folder_name', folder)
    message.success(`已选择 ${files.length} 个文件，保存后将上传到服务器`)
  }

  const saveSource = (): Promise<void> => {
    return sourceForm.validateFields().then(async (v) => {
      const isUpload = v.source_type === 'upload'
      const isApi = v.source_type === 'api'
      const existingCfg = editingSource?.config_json || {}
      const existingCount = Number(existingCfg.file_count || 0)
      if (isUpload && pendingUploadFiles.length === 0 && existingCount <= 0) {
        message.warning('请选择本地文件夹')
        return Promise.reject(new Error('validation'))
      }

      const analysis = resolveApiAnalysis()
      const hasApiDocs =
        Boolean(analysis?.api_simple) ||
        Boolean(existingCfg.api_simple) ||
        Boolean((existingCfg.api_docs as unknown[])?.length)
      if (isApi && !hasApiDocs) {
        message.warning('请先上传第三方接口文档并完成 AI 分析')
        return Promise.reject(new Error('validation'))
      }

      const apiConfigDirty = apiDocList.some((d) => d.text.trim()) || Boolean(analysis)
      if (isApi && apiConfigDirty && !apiConnectResult?.ok) {
        message.warning('请先测试连通，确认接口返回正常后再保存')
        return Promise.reject(new Error('validation'))
      }

      setSourceSaving(true)
      try {
        let config_json: Record<string, unknown>
        if (isUpload) {
          config_json = {
            folder_name: (v.folder_name || existingCfg.folder_name || '').trim(),
            include_subdirs: Boolean(v.include_subdirs),
            file_count: existingCount,
            total_bytes: Number(existingCfg.total_bytes || 0),
            uploaded_at: String(existingCfg.uploaded_at || ''),
          }
        } else if (isApi) {
          config_json = {
            include_subdirs: true,
          }
          if (analysis) {
            config_json.api_simple = {
              ...(analysis.api_simple || {}),
              permission_mapping: permissionMappingDraft,
            }
            config_json.api_analysis = {
              checks: analysis.checks,
              missing_items: analysis.missing_items,
              summary: analysis.summary,
              vendor_name: analysis.vendor_name,
              parse_source: analysis.parse_source,
              ingestion_pipeline: analysis.ingestion_pipeline,
              detected_endpoints: analysis.detected_endpoints,
              minimum_capabilities: analysis.minimum_capabilities,
              permission_mapping_note: analysis.permission_mapping_note,
            }
            config_json.api_docs = analysis.api_docs
          } else if (existingCfg.api_simple) {
            config_json.api_simple = {
              ...(existingCfg.api_simple as Record<string, unknown>),
              permission_mapping: permissionMappingDraft,
            }
            if (existingCfg.api_analysis) config_json.api_analysis = existingCfg.api_analysis
            if (existingCfg.api_docs) config_json.api_docs = existingCfg.api_docs
          } else {
            config_json.api_simple = { permission_mapping: permissionMappingDraft }
          }
          const docsWithText = apiDocList.filter((d) => d.text.trim())
          if (docsWithText.length) {
            config_json.api_docs = docsWithText
          } else if (apiDocList.length) {
            config_json.api_docs = apiDocList.map(({ filename, content_hash }) => ({
              filename,
              content_hash,
            }))
          }
        } else {
          config_json = {
            root_path: (v.root_path || '').trim(),
            include_subdirs: Boolean(v.include_subdirs),
          }
        }

        const body = {
          name: v.name.trim(),
          source_type: v.source_type,
          description: (v.description || '').trim(),
          is_active: v.is_active,
          config_json,
        }
        const res = editingSource
          ? await adminApi.put<SourceRow>(`/admin/ingestion/sources/${editingSource.id}`, body)
          : await adminApi.post<SourceRow>('/admin/ingestion/sources', body)
        const sourceId = editingSource?.id ?? res.data.id
        if (isUpload && pendingUploadFiles.length > 0) {
          await uploadFolderToSource(sourceId, pendingUploadFiles)
          message.success(`已上传 ${pendingUploadFiles.length} 个文件`)
        } else {
          message.success(editingSource ? '已保存' : '已创建')
        }
        resetSourceApiDraft()
        setEditingSource(null)
        setSourceModalOpen(false)
        void loadSources()
      } catch (e) {
        message.error(formatApiError(e, '保存失败'))
        throw e
      } finally {
        setSourceSaving(false)
      }
    })
  }

  const requestCloseSourceModal = () => {
    if (sourceSaving || apiAnalyzeFlowActive) {
      message.warning('正在处理中，请稍候')
      return
    }
    if (!isNewSourceDraftDirty()) {
      setSourceModalOpen(false)
      setEditingSource(null)
      return
    }
    Modal.confirm({
      title: '是否保存当前采集源？',
      content: '关闭后未保存的解析结果与表单内容将清除。',
      okText: '保存',
      cancelText: '不保存',
      onOk: () => saveSource(),
      onCancel: () => {
        resetSourceModalDraft()
        setSourceModalOpen(false)
      },
    })
  }

  const testSource = (id: number) => {
    adminApi
      .post<{ message: string }>(`/admin/ingestion/sources/${id}/test`)
      .then((r) => message.success(r.data.message || '测试成功'))
      .catch((e) => message.error(formatApiError(e, '测试失败')))
  }

  const openCopySource = (row: SourceRow) => {
    setCopySourceTarget(row)
    setCopySourceName(suggestCopySourceName(row.name))
    setCopySourceOpen(true)
  }

  const confirmCopySource = () => {
    if (!copySourceTarget) return Promise.resolve()
    const name = copySourceName.trim()
    if (!name) {
      message.warning('请填写采集源名称')
      return Promise.reject(new Error('empty name'))
    }
    setCopySourceSubmitting(true)
    return adminApi
      .post<SourceRow>(`/admin/ingestion/sources/${copySourceTarget.id}/copy`, { name })
      .then((r) => {
        message.success(`已复制为「${r.data.name}」`)
        setCopySourceOpen(false)
        setCopySourceTarget(null)
        void loadSources()
      })
      .catch((e) => {
        message.error(formatApiError(e, '复制失败'))
        return Promise.reject(e)
      })
      .finally(() => setCopySourceSubmitting(false))
  }

  const deleteSource = (row: SourceRow) => {
    Modal.confirm({
      title: `删除采集源「${row.name}」？`,
      okText: '删除',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: () =>
        adminApi
          .delete(`/admin/ingestion/sources/${row.id}`)
          .then(() => {
            message.success('已删除')
            void loadSources()
          })
          .catch((e) => {
            message.error(formatApiError(e, '删除失败'))
            return Promise.reject(e)
          }),
    })
  }

  const buildScheduleJson = (v: Record<string, unknown>): ScheduleJson => {
    const type = (v.schedule_type as ScheduleJson['type']) || 'none'
    if (type === 'none') return { type: 'none' }
    if (type === 'daily') return { type: 'daily', time: String(v.schedule_time || '02:00') }
    if (type === 'weekly') {
      return {
        type: 'weekly',
        weekday: Number(v.schedule_weekday || 1),
        time: String(v.schedule_time || '02:00'),
      }
    }
    return { type: 'interval', hours: Number(v.schedule_hours || 6) }
  }

  const openTaskCreate = () => {
    setEditingTask(null)
    taskForm.setFieldsValue({
      name: '',
      source_id: sourceOptions[0]?.value,
      scope: 'enterprise',
      dimension_id: undefined,
      schedule_type: 'none',
      schedule_time: '02:00',
      schedule_weekday: 1,
      schedule_hours: 6,
      is_enabled: true,
    })
    setTaskModalOpen(true)
  }

  const openTaskEdit = (row: TaskRow) => {
    setEditingTask(row)
    const s = row.schedule_json || { type: 'none' }
    taskForm.setFieldsValue({
      name: row.name,
      source_id: row.source_id,
      scope: row.scope,
      dimension_id: row.dimension_id ?? undefined,
      schedule_type: s.type || 'none',
      schedule_time: s.time || '02:00',
      schedule_weekday: s.weekday || 1,
      schedule_hours: s.hours || 6,
      is_enabled: row.is_enabled,
    })
    setTaskModalOpen(true)
  }

  const saveTask = () => {
    void taskForm.validateFields().then((v) => {
      const body = {
        name: v.name.trim(),
        source_id: v.source_id,
        scope: v.scope,
        dimension_id: v.dimension_id ?? null,
        schedule_json: buildScheduleJson(v),
        is_enabled: v.is_enabled,
      }
      const req = editingTask
        ? adminApi.put(`/admin/ingestion/tasks/${editingTask.id}`, body)
        : adminApi.post('/admin/ingestion/tasks', body)
      req
        .then(() => {
          message.success(editingTask ? '已保存' : '已创建')
          setTaskModalOpen(false)
          void loadTasks()
        })
        .catch((e) => message.error(formatApiError(e, '保存失败')))
    })
  }

  const executeRun = (row: TaskRow, force = false) => {
    setRunningTaskId(row.id)
    return adminApi
      .post<RunRow>(`/admin/ingestion/tasks/${row.id}/run`, null, {
        params: force ? { force: true } : undefined,
      })
      .then((r) => {
        message.info('已提交采集，请查看运行记录')
        setActiveRun(r.data)
        setTab('runs')
        void loadRuns()
      })
      .catch((e) => message.error(formatApiError(e, '执行失败')))
      .finally(() => setRunningTaskId(null))
  }

  const runTask = (row: TaskRow) => {
    void (async () => {
      let active: RunRow | undefined
      if (activeRun?.task_id === row.id && RUN_ACTIVE.has(activeRun.status)) {
        active = activeRun
      } else {
        try {
          const r = await adminApi.get<PageResult<RunRow>>('/admin/ingestion/runs', {
            params: { task_id: row.id, page: 1, page_size: 5 },
          })
          active = (r.data.items || []).find((x) => RUN_ACTIVE.has(x.status))
        } catch {
          /* 查询失败时仍尝试提交，由后端兜底 */
        }
      }
      if (active) {
        Modal.confirm({
          title: '当前有进行中的采集任务',
          content: `任务「${row.name}」已有运行中的采集（#${active.id}${active.status_message ? `：${active.status_message}` : ''}）。再次触发将并行执行，可能产生重复条目。是否仍要执行？`,
          okText: '仍要执行',
          cancelText: '取消',
          onOk: () => executeRun(row, true),
        })
        return
      }
      void executeRun(row, false)
    })()
  }

  const deleteTask = (row: TaskRow) => {
    Modal.confirm({
      title: `删除采集任务「${row.name}」？`,
      okText: '删除',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: () =>
        adminApi
          .delete(`/admin/ingestion/tasks/${row.id}`)
          .then(() => {
            message.success('已删除')
            void loadTasks()
          })
          .catch((e) => {
            message.error(formatApiError(e, '删除失败'))
            return Promise.reject(e)
          }),
    })
  }

  const openRunDetail = (row: RunRow) => {
    setRunDetailRun(row)
    setRunDetailJobPage(1)
  }

  const deleteRun = (row: RunRow) => {
    Modal.confirm({
      title: `删除运行记录 #${row.id}？`,
      content: '将同时删除该次运行下的全部文件明细记录（不含已入库的知识条目）。此操作不可恢复。',
      okText: '删除',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: () =>
        adminApi
          .delete(`/admin/ingestion/runs/${row.id}`)
          .then(() => {
            message.success('已删除运行记录及文件明细')
            if (runDetailRun?.id === row.id) setRunDetailRun(null)
            if (activeRun?.id === row.id) setActiveRun(null)
            void loadRuns()
          })
          .catch((e) => {
            message.error(formatApiError(e, '删除失败'))
            return Promise.reject(e)
          }),
    })
  }

  const runDetailJobColumns = useMemo(
    () => [
      { title: '文件', dataIndex: 'file_name', ellipsis: true },
      {
        title: '大小',
        dataIndex: 'file_size_bytes',
        width: 100,
        render: (n: number) => formatBytes(n),
      },
      {
        title: '状态',
        dataIndex: 'status',
        width: 88,
        render: (s: string) => jobStatusTag(s),
      },
      { title: '分类', dataIndex: 'category_path', ellipsis: true, render: (t: string) => t || '—' },
      {
        title: '条目',
        dataIndex: 'item_id',
        width: 72,
        render: (id: number | null) => (id ? `#${id}` : '—'),
      },
      {
        title: '错误',
        dataIndex: 'error_message',
        ellipsis: true,
        render: (t: string) => t || '—',
      },
    ],
    [],
  )

  const scheduleType = Form.useWatch('schedule_type', taskForm)
  const taskScope = Form.useWatch('scope', taskForm) as string | undefined

  const loadDimensionsForScope = useCallback((scope: string) => {
    setDimensionsLoading(true)
    return adminApi
      .get<{ items: DimensionOption[] }>('/admin/knowledge/folder-dimensions', {
        params: { scope },
      })
      .then((r) => (r.data.items || []).filter((d) => !d.is_manual))
      .catch(() => [] as DimensionOption[])
      .finally(() => setDimensionsLoading(false))
  }, [])

  useEffect(() => {
    if (!taskModalOpen) return
    const scope = taskScope || 'enterprise'
    void loadDimensionsForScope(scope).then((items) => {
      setDimensions(items)
      const cur = taskForm.getFieldValue('dimension_id') as number | undefined
      if (cur != null && items.some((d) => d.id === cur)) return
      taskForm.setFieldValue('dimension_id', items[0]?.id)
    })
  }, [taskModalOpen, taskScope, loadDimensionsForScope, taskForm])

  const dimensionOptions = useMemo(
    () => dimensions.map((d) => ({ value: d.id, label: d.name })),
    [dimensions],
  )

  return (
    <div>
      <PageHeader
        title="文件采集"
        description="配置采集源与采集任务：采集阶段文件先入知识库「采集文件夹」，再异步解析、智能分类并归类入库。"
      />

      {activeRun && RUN_ACTIVE.has(activeRun.status) ? (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={`采集进行中：${activeRun.task_name || `任务 #${activeRun.task_id}`}`}
          description={
            <Space direction="vertical" style={{ width: '100%' }} size={8}>
              <Typography.Text type="secondary">{activeRun.status_message}</Typography.Text>
              <div>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  采集进度（入采集文件夹）
                </Typography.Text>
                <Progress
                  percent={activeRun.collect_progress ?? 0}
                  size="small"
                  status={activeRun.status === 'collecting' ? 'active' : undefined}
                />
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {activeRun.collected_files ?? 0}/{activeRun.total_files} 个文件 ·{' '}
                  {formatBytes(activeRun.collected_bytes ?? 0)}/{formatBytes(activeRun.total_bytes)}
                </Typography.Text>
              </div>
              <div>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  解析入库进度
                </Typography.Text>
                <Progress
                  percent={activeRun.process_progress ?? 0}
                  size="small"
                  status={activeRun.status === 'processing' ? 'active' : undefined}
                />
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {activeRun.processed_files}/{Math.max(0, activeRun.total_files - activeRun.skipped_files)} 个文件
                  {activeRun.failed_files ? ` · 失败 ${activeRun.failed_files}` : ''}
                </Typography.Text>
              </div>
            </Space>
          }
        />
      ) : null}

      <Card>
        <Tabs
          activeKey={tab}
          onChange={setTab}
          items={[
            {
              key: 'sources',
              label: '采集源',
              children: (
                <>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
                    <Button type="primary" icon={<PlusOutlined />} onClick={openSourceCreate}>
                      新建采集源
                    </Button>
                  </div>
                  <Table<SourceRow>
                    rowKey="id"
                    loading={sourceLoading}
                    dataSource={sources}
                    pagination={tablePagination(sourcePage, sourceTotal, setSourcePage)}
                    scroll={{ x: 900 }}
                    columns={[
                      { title: '名称', dataIndex: 'name', ellipsis: true, width: 160 },
                      {
                        title: '类型',
                        dataIndex: 'source_type',
                        width: 140,
                        render: (t: string) => SOURCE_TYPE_LABEL[t] || t,
                      },
                      {
                        title: '路径 / 文件夹',
                        ellipsis: true,
                        width: 220,
                        render: (_, r) => {
                          if (r.source_type === 'upload') {
                            const folder = String(r.config_json?.folder_name || '')
                            const count = Number(r.config_json?.file_count || 0)
                            if (folder) return `${folder}（${count} 个文件）`
                            return count > 0 ? `${count} 个文件` : '—'
                          }
                          return String(r.config_json?.root_path || '—')
                        },
                      },
                      {
                        title: '状态',
                        width: 80,
                        render: (_, r) => (
                          <Tag color={r.is_active ? 'green' : 'default'}>{r.is_active ? '启用' : '停用'}</Tag>
                        ),
                      },
                      { title: '任务数', dataIndex: 'task_count', width: 80 },
                      {
                        title: '操作',
                        width: 300,
                        fixed: 'right',
                        render: (_, row) => (
                          <Space wrap>
                            <Button size="small" onClick={() => openSourceEdit(row)}>
                              编辑
                            </Button>
                            <Button size="small" icon={<CopyOutlined />} onClick={() => openCopySource(row)}>
                              复制
                            </Button>
                            {isRunnableSource(row.source_type) ? (
                              <Button size="small" onClick={() => testSource(row.id)}>
                                测试
                              </Button>
                            ) : null}
                            <Button size="small" danger onClick={() => deleteSource(row)}>
                              删除
                            </Button>
                          </Space>
                        ),
                      },
                    ]}
                  />
                </>
              ),
            },
            {
              key: 'tasks',
              label: '采集任务',
              children: (
                <>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
                    <Button type="primary" icon={<PlusOutlined />} onClick={openTaskCreate}>
                      新建任务
                    </Button>
                  </div>
                  <Table<TaskRow>
                    rowKey="id"
                    loading={taskLoading}
                    dataSource={tasks}
                    pagination={tablePagination(taskPage, taskTotal, setTaskPage)}
                    scroll={{ x: 980 }}
                    columns={[
                      { title: '任务名称', dataIndex: 'name', ellipsis: true, width: 160 },
                      { title: '采集源', dataIndex: 'source_name', ellipsis: true, width: 140 },
                      {
                        title: '目标知识库',
                        dataIndex: 'scope',
                        width: 128,
                        render: (s: string) => SCOPE_LABEL[s] || s,
                      },
                      { title: '虚拟分类', dataIndex: 'dimension_name', ellipsis: true, width: 140 },
                      {
                        title: '定时',
                        width: 120,
                        render: (_, r) => scheduleSummary(r.schedule_json),
                        ellipsis: true,
                      },
                      {
                        title: '状态',
                        width: 80,
                        render: (_, r) => (
                          <Tag color={r.is_enabled ? 'green' : 'default'}>{r.is_enabled ? '启用' : '停用'}</Tag>
                        ),
                      },
                      {
                        title: '操作',
                        width: 260,
                        fixed: 'right',
                        render: (_, row) => (
                          <Space wrap>
                            <Button size="small" onClick={() => openTaskEdit(row)}>
                              编辑
                            </Button>
                            <Button
                              size="small"
                              type="primary"
                              icon={<ThunderboltOutlined />}
                              loading={runningTaskId === row.id}
                              onClick={() => runTask(row)}
                            >
                              立即执行
                            </Button>
                            <Button size="small" danger onClick={() => deleteTask(row)}>
                              删除
                            </Button>
                          </Space>
                        ),
                      },
                    ]}
                  />
                </>
              ),
            },
            {
              key: 'runs',
              label: '运行记录',
              children: (
                <>
                  <Space style={{ marginBottom: 12 }} wrap>
                    <Typography.Text type="secondary">筛选任务</Typography.Text>
                    <Select
                      allowClear
                      placeholder="全部任务"
                      style={{ width: 220 }}
                      options={taskOptions}
                      value={filterRunTaskId}
                      onChange={(v) => {
                        setFilterRunTaskId(v)
                        setRunPage(1)
                      }}
                    />
                  </Space>
                  <Table<RunRow>
                    rowKey="id"
                    loading={runLoading}
                    dataSource={runs}
                    pagination={tablePagination(runPage, runTotal, setRunPage)}
                    columns={[
                      { title: '任务', dataIndex: 'task_name', ellipsis: true },
                      {
                        title: '开始时间',
                        dataIndex: 'started_at',
                        width: 168,
                        render: (t: string | null) => formatDate(t) || '—',
                      },
                      {
                        title: '触发',
                        dataIndex: 'trigger_type',
                        width: 80,
                        render: (t: string) => runTriggerLabel(t),
                      },
                      {
                        title: '状态',
                        dataIndex: 'status',
                        width: 96,
                        render: (s: string) => runStatusTag(s),
                      },
                      {
                        title: '采集',
                        width: 120,
                        render: (_, r) => (
                          <Progress
                            percent={r.collect_progress ?? 0}
                            size="small"
                            format={() => `${r.collected_files ?? 0}/${r.total_files}`}
                          />
                        ),
                      },
                      {
                        title: '解析入库',
                        width: 120,
                        render: (_, r) => (
                          <Progress
                            percent={r.process_progress ?? 0}
                            size="small"
                            status={r.status === 'failed' ? 'exception' : undefined}
                            format={() =>
                              `${r.processed_files}/${Math.max(0, r.total_files - r.skipped_files)}`
                            }
                          />
                        ),
                      },
                      {
                        title: '说明',
                        dataIndex: 'status_message',
                        ellipsis: true,
                        render: (t: string) => t || '—',
                      },
                      {
                        title: '操作',
                        width: 148,
                        render: (_, row) => (
                          <Space size={4}>
                            <Button size="small" onClick={() => openRunDetail(row)}>
                              查看明细
                            </Button>
                            <Button
                              size="small"
                              danger
                              disabled={RUN_ACTIVE.has(row.status)}
                              onClick={() => deleteRun(row)}
                            >
                              删除
                            </Button>
                          </Space>
                        ),
                      },
                    ]}
                  />
                </>
              ),
            },
          ]}
        />
      </Card>

      <Modal
        title={
          runDetailRun
            ? `文件明细 · ${formatRunOptionLabel(runDetailRun)}`
            : '文件明细'
        }
        open={runDetailRun != null}
        onCancel={() => setRunDetailRun(null)}
        footer={null}
        width={920}
        destroyOnClose
      >
        {runDetailRun ? (
          <Table<FileJobRow>
            rowKey="id"
            size="small"
            loading={runDetailJobLoading}
            dataSource={runDetailJobs}
            columns={runDetailJobColumns}
            pagination={tablePagination(runDetailJobPage, runDetailJobTotal, setRunDetailJobPage)}
            scroll={{ x: 760 }}
          />
        ) : null}
      </Modal>

      <AppFormModal
        title="复制采集源"
        open={copySourceOpen}
        bodyHeight={168}
        onCancel={() => {
          setCopySourceOpen(false)
          setCopySourceTarget(null)
          setCopySourceSubmitting(false)
        }}
        onOk={() => confirmCopySource()}
        confirmLoading={copySourceSubmitting}
        okText="复制"
      >
        <Typography.Text style={{ display: 'block', marginBottom: 8 }}>
          复制自：<strong>{copySourceTarget?.name || '—'}</strong>
        </Typography.Text>
        <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 8, fontSize: 12 }}>
          新采集源名称
        </Typography.Text>
        <Input
          maxLength={64}
          value={copySourceName}
          onChange={(e) => setCopySourceName(e.target.value)}
          placeholder="采集源名称"
        />
      </AppFormModal>

      <AppFormModal
        title={editingSource ? '编辑采集源' : '新建采集源'}
        open={sourceModalOpen}
        onCancel={requestCloseSourceModal}
        onOk={() => void saveSource()}
        okText="保存"
        confirmLoading={sourceSaving}
        bodyHeight={sourceType === 'api' ? 520 : 360}
        className={sourceType === 'api' ? 'app-form-modal--scroll-body' : undefined}
        width={sourceType === 'api' ? 640 : undefined}
      >
        <input
          ref={folderInputRef}
          type="file"
          style={{ display: 'none' }}
          multiple
          {...({ webkitdirectory: '', directory: '' } as React.InputHTMLAttributes<HTMLInputElement>)}
          onChange={(e) => {
            handleUploadFolderChange(e.target.files)
            e.target.value = ''
          }}
        />
        <Form form={sourceForm} layout="vertical" size="small">
          <Row gutter={12}>
            <Col span={14}>
              <Form.Item name="name" label="名称" rules={[{ required: true, message: '请输入名称' }]}>
                <Input maxLength={64} placeholder="如：制度文档包" />
              </Form.Item>
            </Col>
            <Col span={10}>
              <Form.Item name="source_type" label="类型" rules={[{ required: true }]}>
                <Select
                  options={[
                    { value: 'upload', label: '本地文件夹' },
                    { value: 'nas', label: 'NAS / 网络目录' },
                    { value: 'api', label: 'API 接口' },
                    { value: 'ftp', label: 'FTP（即将支持）', disabled: true },
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>
          {sourceType === 'upload' ? (
            <>
              <Form.Item name="folder_name" hidden>
                <Input />
              </Form.Item>
              <Form.Item label="本地文件夹" required style={{ marginBottom: 8 }}>
                <Space align="center" wrap>
                  <Button size="small" icon={<FolderOpenOutlined />} onClick={pickUploadFolder}>
                    选择文件夹
                  </Button>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {pendingUploadFiles.length > 0
                      ? `待上传 ${pendingUploadFiles.length} 个文件`
                      : editingSource && Number(editingSource.config_json?.file_count || 0) > 0
                        ? `已上传 ${Number(editingSource.config_json?.file_count || 0)} 个`
                        : '未选择'}
                  </Typography.Text>
                </Space>
              </Form.Item>
            </>
          ) : sourceType === 'api' ? (
            <div className="app-form-modal__section ingestion-api-source-panel">
              <div className="app-form-modal__step">
                <Typography.Text className="app-form-modal__section-title">1. 上传接口文档</Typography.Text>
                <Typography.Text className="app-form-modal__step-label">
                  可多份上传；勿重复同一文档；Token 勿写入文档。上传后不会立即分析，需手动执行下一步。
                </Typography.Text>
                <Button
                  size="small"
                  icon={<UploadOutlined />}
                  onClick={() => apiDocInputRef.current?.click()}
                  style={{ marginTop: 4 }}
                >
                  上传文档
                </Button>
              </div>
              <div className="app-form-modal__step ingestion-api-docs-panel">
                <Typography.Text className="app-form-modal__section-title">已上传文档</Typography.Text>
                {apiDocList.length ? (
                  <Table<ApiDocRow>
                    size="small"
                    rowKey="content_hash"
                    pagination={false}
                    dataSource={apiDocList}
                    columns={[
                      { title: '文件名', dataIndex: 'filename', ellipsis: true },
                      {
                        title: '状态',
                        width: 88,
                        render: (_, row) => (
                          <Tag color={row.status === 'analyzed' ? 'success' : 'default'}>
                            {row.status === 'analyzed' ? '已分析' : '待分析'}
                          </Tag>
                        ),
                      },
                      {
                        title: '操作',
                        width: 64,
                        render: (_, row) => (
                          <Popconfirm
                            title={`删除「${row.filename}」？删除后需重新上传并分析。`}
                            onConfirm={() => void removeApiDoc(row)}
                          >
                            <Button type="link" size="small" danger>
                              删除
                            </Button>
                          </Popconfirm>
                        ),
                      },
                    ]}
                  />
                ) : (
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    尚未上传接口文档
                  </Typography.Text>
                )}
              </div>
              <div className="app-form-modal__step">
                <Typography.Text className="app-form-modal__section-title">2. AI 分析并测试连通</Typography.Text>
                <Typography.Text className="app-form-modal__step-label">
                  每次点击将对上方<strong>全部</strong>文档重新 AI 解析、组装请求参数，再<strong>单次</strong>
                  调用真实接口；最后由 AI 对照文档与 HTTP 日志分析调用顺序与参数（如 corpid）是否正确，不自动多轮重试。
                </Typography.Text>
                <Space wrap size={8} style={{ marginTop: 4 }}>
                  <Button
                    size="small"
                    type="primary"
                    loading={apiDocParsing}
                    disabled={!apiDocList.length || apiAnalyzeFlowActive}
                    onClick={runAnalyzeApiDocs}
                  >
                    AI 分析并测试连通
                  </Button>
                  {apiAnalysis?.manual_fields?.length ? (
                    <Button
                      size="small"
                      onClick={() => {
                        void promptApiManualFields(apiAnalysis.manual_fields || []).then((filled) => {
                          if (!filled || !apiAnalysis) return
                          setApiAnalysis(applyManualFieldsToAnalysis(apiAnalysis, filled))
                          message.success('参数已更新，请再次点击「AI 分析并测试连通」执行探测')
                        })
                      }}
                    >
                      补全参数
                    </Button>
                  ) : null}
                  {apiConnectResult?.call_logs?.length ? (
                    <Button
                      size="small"
                      icon={<FileTextOutlined />}
                      onClick={() => setApiConnectLogOpen(true)}
                    >
                      查看日志
                    </Button>
                  ) : null}
                </Space>
                {apiAnalyzeSteps.length ? (
                  <Steps
                    size="small"
                    direction="vertical"
                    style={{ marginTop: 12 }}
                    items={apiAnalyzeSteps.map((step) => ({
                      title: step.title,
                      description: step.description,
                      status: apiAnalyzeFlowActive
                        ? step.status
                        : step.status === 'process'
                          ? 'wait'
                          : step.status,
                    }))}
                  />
                ) : null}
                {apiAnalysis?.ingestion_pipeline?.length ? (
                  <>
                    <Typography.Text className="app-form-modal__section-title" style={{ display: 'block', marginTop: 12 }}>
                      采集调用顺序
                    </Typography.Text>
                    <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                      按文档入参/出参串联：上一步响应 id → 下一步请求参数；探测时会用真实响应依次填充并调用，全部贯通后才判定连通。
                    </Typography.Text>
                    <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12 }}>
                      {apiAnalysis.ingestion_pipeline.map((row) => (
                        <li key={`${row.step}-${row.role || row.name}`} style={{ marginBottom: 4 }}>
                          <Typography.Text>
                            {row.step}. {row.name}
                            {row.required === false ? (
                              <Typography.Text type="secondary">（可选）</Typography.Text>
                            ) : null}
                          </Typography.Text>
                          {row.purpose ? (
                            <Typography.Text type="secondary" style={{ display: 'block' }}>
                              {row.purpose}
                            </Typography.Text>
                          ) : null}
                          {row.url ? (
                            <Typography.Text type="secondary" code style={{ fontSize: 11, wordBreak: 'break-all' }}>
                              {row.method ? `${row.method} ` : ''}
                              {row.url}
                            </Typography.Text>
                          ) : null}
                          {row.depends_on ? (
                            <Typography.Text type="secondary" style={{ display: 'block', fontSize: 11 }}>
                              依赖：{row.depends_on}
                            </Typography.Text>
                          ) : null}
                          {row.inputs?.length ? (
                            <Typography.Text type="secondary" style={{ display: 'block', fontSize: 11 }}>
                              入参：
                              {row.inputs
                                .map((inp) =>
                                  inp.from_output && inp.param && inp.from_output !== inp.param
                                    ? `${inp.param}←${inp.from_output}`
                                    : inp.param || inp.from_output,
                                )
                                .filter(Boolean)
                                .join('、')}
                            </Typography.Text>
                          ) : null}
                          {row.outputs?.length ? (
                            <Typography.Text type="secondary" style={{ display: 'block', fontSize: 11 }}>
                              出参：
                              {row.outputs
                                .map((out) => out.save_as || out.fields?.join('/'))
                                .filter(Boolean)
                                .join('、')}
                            </Typography.Text>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  </>
                ) : null}
                {apiAnalysis?.checks?.length ? (
                  <>
                    <Typography.Text className="app-form-modal__section-title" style={{ display: 'block', marginTop: 12 }}>
                      配置项匹配
                    </Typography.Text>
                    <ul className="ingestion-api-check-list">
                      {apiAnalysis.checks.map((c) => (
                        <li key={c.key} className="ingestion-api-check-list__item">
                          <div className="ingestion-api-check-list__head">
                            <Typography.Text>{c.label}</Typography.Text>
                            {renderCheckTag(c.found)}
                          </div>
                          {c.detail ? (
                            <Typography.Text type="secondary" className="ingestion-api-check-list__detail">
                              {c.detail}
                            </Typography.Text>
                          ) : null}
                          {c.key === 'auth_token' &&
                          String(apiAnalysis.api_simple?.auth_description || '').trim() ? (
                            <Typography.Text type="secondary" className="ingestion-api-check-list__detail">
                              {String(apiAnalysis.api_simple.auth_description)}
                            </Typography.Text>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  </>
                ) : null}
                {(apiAnalysis || editingSource?.config_json?.api_simple) && sourceForm.getFieldValue('source_type') === 'api' ? (
                  <>
                    <Typography.Text className="app-form-modal__section-title" style={{ display: 'block', marginTop: 12 }}>
                      权限入库规则
                    </Typography.Text>
                    <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
                      权限由接口文档分析结果自动带入采集流程；入库时按本系统人员、部门、角色名称等值匹配，成功、失败和重复权限都会写入采集任务日志。
                    </Typography.Text>
                    {apiAnalysis?.permission_mapping_note ? (
                      <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
                        {apiAnalysis.permission_mapping_note}
                      </Typography.Text>
                    ) : null}
                  </>
                ) : null}
                {apiProbeReview?.param_issues?.length && !apiProbeReview.probe_ok ? (
                  <Alert
                    type="warning"
                    showIcon
                    style={{ marginTop: 8 }}
                    message="参数与文档不一致"
                    description={
                      <ul style={{ margin: 0, paddingLeft: 18 }}>
                        {apiProbeReview.param_issues.slice(0, 8).map((issue) => (
                          <li key={issue.field}>
                            {issue.field}
                            {issue.current ? `：当前「${issue.current}」` : ''}
                            {issue.expected_from_doc ? `，文档应为「${issue.expected_from_doc}」` : ''}
                            {issue.fix ? ` — ${issue.fix}` : ''}
                          </li>
                        ))}
                      </ul>
                    }
                  />
                ) : null}
                {apiConnectResult ? (
                  <div className="ingestion-api-connect-result">
                    <Typography.Text type={apiConnectResult.ok ? 'success' : 'danger'}>
                      {apiConnectResult.message}
                    </Typography.Text>
                    {apiConnectResult.steps?.length ? (
                      <ul className="ingestion-api-check-list">
                        {apiConnectResult.steps.map((step) => (
                          <li key={step.key} className="ingestion-api-check-list__item">
                            <div className="ingestion-api-check-list__head">
                              <Typography.Text>{step.label}</Typography.Text>
                              {renderStepTag(step.status)}
                            </div>
                            {step.message ? (
                              <Typography.Text
                                type={step.status === 'fail' ? 'danger' : 'secondary'}
                                className="ingestion-api-check-list__detail"
                              >
                                {step.message}
                              </Typography.Text>
                            ) : null}
                          </li>
                        ))}
                      </ul>
                    ) : null}
                    {apiConnectResult.call_graph?.nodes?.length ? (
                      <Typography.Text type="secondary" style={{ display: 'block', marginTop: 6, fontSize: 12 }}>
                        已识别 {apiConnectResult.call_graph.nodes.length} 个接口，实际发起{' '}
                        {apiConnectResult.call_logs?.length || 0} 次 HTTP 调用；未发起的接口通常是缺少上游出参或文档未给可用 URL。
                      </Typography.Text>
                    ) : null}
                    {apiConnectResult.preview?.length ? (
                      <Typography.Text type="secondary" style={{ display: 'block', marginTop: 6, fontSize: 12 }}>
                        示例文件：{apiConnectResult.preview.map((p) => p.name).slice(0, 3).join('、')}
                      </Typography.Text>
                    ) : null}
                  </div>
                ) : null}
              </div>
              <input
                ref={apiDocInputRef}
                type="file"
                accept={API_DOC_ACCEPT}
                multiple
                style={{ display: 'none' }}
                onChange={(e) => {
                  void handleApiDocFileChange(e.target.files)
                  e.target.value = ''
                }}
              />
              <AppFormModal
                title="连通测试调用日志"
                open={apiConnectLogOpen}
                onCancel={() => setApiConnectLogOpen(false)}
                footer={null}
                width={720}
                bodyHeight={480}
                compact={false}
                className="app-form-modal--log-viewer"
                hint="以下为接口探测实际发出的 HTTP 请求与响应（完整入参/返回体）。请注意日志中可能含 Token 等敏感信息。"
              >
                {apiConnectResult?.call_graph?.nodes?.length ? (
                  <div style={{ marginBottom: 12 }}>
                    <Typography.Text className="ingestion-api-probe-log__label">接口探测链路</Typography.Text>
                    <Collapse
                      size="small"
                      className="ingestion-api-probe-log"
                      items={apiConnectResult.call_graph.nodes.map((node) => {
                        const outputs = Object.entries(node.actual_outputs || {}).filter(([, value]) => value != null && value !== '')
                        const bindings = node.input_bindings || []
                        return {
                          key: `graph-${node.step}-${node.role || node.label}`,
                          label: (
                            <Space size={8} wrap>
                              <Typography.Text strong>{`#${node.step} ${node.label}`}</Typography.Text>
                              {renderStepTag(normalizeProbeStatus(node.status))}
                              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                {node.called ? '已调用' : '未调用'} · {node.method || node.request?.method || 'GET'}
                              </Typography.Text>
                            </Space>
                          ),
                          children: (
                            <div className="ingestion-api-probe-log__body">
                              <Typography.Text className="ingestion-api-probe-log__url">
                                {node.request?.url || node.url || '(未识别 URL)'}
                              </Typography.Text>
                              {node.reason ? (
                                <Typography.Text type={node.status === 'fail' ? 'danger' : 'secondary'} style={{ display: 'block', marginTop: 6, fontSize: 12 }}>
                                  {node.reason}
                                </Typography.Text>
                              ) : null}
                              {node.missing_inputs?.length ? (
                                <Alert
                                  type="warning"
                                  showIcon
                                  style={{ marginTop: 8 }}
                                  message={`缺少入参：${node.missing_inputs.join('、')}`}
                                />
                              ) : null}
                              {bindings.length ? (
                                <>
                                  <Typography.Text className="ingestion-api-probe-log__label">入参来源</Typography.Text>
                                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                                    {bindings.map((binding, idx) => (
                                      <li key={`${binding.param || idx}-${binding.from_output || ''}`}>
                                        <Typography.Text type={binding.available ? 'secondary' : 'danger'} style={{ fontSize: 12 }}>
                                          {binding.available
                                            ? `#${binding.source_step} ${binding.source_label || ''} 返回 ${binding.from_output || binding.param} → 本接口入参 ${binding.param || ''}`
                                            : `未找到上游返回 ${binding.from_output || binding.param}，无法填入 ${binding.param || ''}`}
                                        </Typography.Text>
                                      </li>
                                    ))}
                                  </ul>
                                </>
                              ) : null}
                              {outputs.length ? (
                                <>
                                  <Typography.Text className="ingestion-api-probe-log__label">本接口实际出参</Typography.Text>
                                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                                    {outputs.map(([key, value]) => (
                                      <li key={key}>
                                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                          {key} = {String(value)}
                                        </Typography.Text>
                                      </li>
                                    ))}
                                  </ul>
                                </>
                              ) : null}
                            </div>
                          ),
                        }
                      })}
                    />
                    {apiConnectResult.call_graph.edges?.length ? (
                      <div style={{ marginTop: 8 }}>
                        <Typography.Text className="ingestion-api-probe-log__label">参数传递关系</Typography.Text>
                        <ul style={{ margin: 0, paddingLeft: 18 }}>
                          {apiConnectResult.call_graph.edges
                            .filter((edge) => edge.param || edge.from_output)
                            .map((edge, idx) => (
                              <li key={`${edge.from_step || 'missing'}-${edge.to_step || idx}-${edge.param || idx}`}>
                                <Typography.Text type={edge.matched === false ? 'danger' : 'secondary'} style={{ fontSize: 12 }}>
                                  {edge.matched === false
                                    ? `缺少 ${edge.from_output || edge.param}，无法调用 #${edge.to_step || ''} ${edge.to_label || ''}`
                                    : `#${edge.from_step} ${edge.from_label || ''} 的 ${edge.from_output || ''} → #${edge.to_step} ${edge.to_label || ''} 的 ${edge.param || ''}`}
                                </Typography.Text>
                              </li>
                            ))}
                        </ul>
                      </div>
                    ) : null}
                  </div>
                ) : null}
                {apiConnectResult?.call_logs?.length ? (
                  <Collapse
                    accordion
                    size="small"
                    className="ingestion-api-probe-log"
                    items={apiConnectResult.call_logs.map((log) => {
                      const phaseLabel = API_PROBE_PHASE_LABEL[log.phase] || log.phase
                      const statusText =
                        log.response_status != null ? String(log.response_status) : log.error ? '失败' : '-'
                      return {
                        key: String(log.index),
                        label: (
                          <Space size={8} wrap>
                            <Typography.Text strong>{`#${log.index} ${log.label}`}</Typography.Text>
                            <Tag color={log.ok ? 'success' : 'error'}>{log.ok ? '成功' : '失败'}</Tag>
                            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                              {phaseLabel} · {log.method} · HTTP {statusText}
                              {log.elapsed_ms != null ? ` · ${log.elapsed_ms}ms` : ''}
                            </Typography.Text>
                          </Space>
                        ),
                        children: (
                          <div className="ingestion-api-probe-log__body">
                            <Typography.Text className="ingestion-api-probe-log__url">{log.url}</Typography.Text>
                            {log.error ? (
                              <Alert type="error" showIcon message={log.error} style={{ marginTop: 8 }} />
                            ) : null}
                            {log.request_query && Object.keys(log.request_query).length ? (
                              <>
                                <Typography.Text className="ingestion-api-probe-log__label">Query 参数</Typography.Text>
                                <pre className="ingestion-api-probe-log__pre">
                                  {JSON.stringify(log.request_query, null, 2)}
                                </pre>
                              </>
                            ) : null}
                            {log.request_body != null &&
                            typeof log.request_body === 'object' &&
                            Object.keys(log.request_body as object).length ? (
                              <>
                                <Typography.Text className="ingestion-api-probe-log__label">请求 Body</Typography.Text>
                                <pre className="ingestion-api-probe-log__pre">
                                  {JSON.stringify(log.request_body, null, 2)}
                                </pre>
                              </>
                            ) : null}
                            {log.request_headers && Object.keys(log.request_headers).length ? (
                              <>
                                <Typography.Text className="ingestion-api-probe-log__label">请求头</Typography.Text>
                                <pre className="ingestion-api-probe-log__pre">
                                  {JSON.stringify(log.request_headers, null, 2)}
                                </pre>
                              </>
                            ) : null}
                            <Typography.Text className="ingestion-api-probe-log__label">响应 Body</Typography.Text>
                            <pre className="ingestion-api-probe-log__pre">
                              {log.response_body?.trim() || '(无响应体)'}
                            </pre>
                          </div>
                        ),
                      }
                    })}
                  />
                ) : (
                  <Typography.Text type="secondary">暂无调用记录</Typography.Text>
                )}
              </AppFormModal>
            </div>
          ) : (
            <Form.Item
              name="root_path"
              label="根目录路径"
              rules={[{ required: true, message: '请输入挂载目录路径' }]}
            >
              <Input placeholder="如 Z:\incoming 或 /mnt/nas/inbox" />
            </Form.Item>
          )}
          <Row gutter={12} align="middle">
            {sourceType === 'nas' || sourceType === 'upload' ? (
              <Col span={12}>
                <Form.Item name="include_subdirs" label="包含子目录" valuePropName="checked" style={{ marginBottom: 8 }}>
                  <Switch size="small" />
                </Form.Item>
              </Col>
            ) : null}
            <Col span={sourceType === 'nas' || sourceType === 'upload' ? 12 : 24}>
              <Form.Item name="is_active" label="启用" valuePropName="checked" style={{ marginBottom: 8 }}>
                <Switch size="small" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="description" label="说明" style={{ marginBottom: 0 }}>
            <Input maxLength={512} placeholder="可选" />
          </Form.Item>
        </Form>
      </AppFormModal>

      <AppFormModal
        title="补全接口探测参数"
        open={apiManualFieldsOpen}
        onCancel={() => closeApiManualFields(null)}
        onOk={confirmApiManualFields}
        okText="确认并继续探测"
        bodyHeight={Math.min(420, 120 + apiManualFieldsDraft.length * 56)}
        width={560}
        zIndex={1050}
      >
        <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 12, fontSize: 12 }}>
          AI 未能从文档中确定以下参数，请按客户环境手动填写后再进行连通探测。
        </Typography.Text>
        {(['OAuth 鉴权', '业务接口', '环境'] as const).map((section) => {
          const rows = apiManualFieldsDraft.filter((f) => f.section === section)
          if (!rows.length) return null
          return (
            <div key={section} style={{ marginBottom: 12 }}>
              <Typography.Text strong style={{ fontSize: 12 }}>
                {section}
              </Typography.Text>
              {rows.map((field) => (
                <Form.Item
                  key={field.key}
                  label={field.label}
                  required={field.required}
                  style={{ marginTop: 8, marginBottom: 0 }}
                  tooltip={field.hint}
                >
                  <Input
                    size="small"
                    maxLength={256}
                    type={field.secret ? 'password' : 'text'}
                    placeholder={field.hint}
                    value={apiManualFieldValues[field.key] || ''}
                    onChange={(e) =>
                      setApiManualFieldValues((prev) => ({ ...prev, [field.key]: e.target.value }))
                    }
                  />
                </Form.Item>
              ))}
            </div>
          )
        })}
      </AppFormModal>

      <AppFormModal
        title={editingTask ? '编辑采集任务' : '新建采集任务'}
        open={taskModalOpen}
        onCancel={() => setTaskModalOpen(false)}
        onOk={saveTask}
        okText="保存"
        bodyHeight={360}
      >
        <Form form={taskForm} layout="vertical" size="small">
          <Form.Item name="name" label="任务名称" rules={[{ required: true, message: '请输入任务名称' }]}>
            <Input maxLength={64} />
          </Form.Item>
          <Form.Item name="source_id" label="采集源" rules={[{ required: true, message: '请选择采集源' }]}>
            <Select options={sourceOptions} placeholder="选择采集源" />
          </Form.Item>
          <Form.Item name="scope" label="目标知识库" rules={[{ required: true }]}>
            <Select options={SCOPE_OPTIONS} />
          </Form.Item>
          <Form.Item
            name="dimension_id"
            label="虚拟分类"
            rules={[{ required: true, message: '请选择虚拟分类' }]}
            extra={
              !dimensionsLoading && dimensionOptions.length === 0
                ? '请先在「智能分类 → 虚拟分类」中创建'
                : undefined
            }
          >
            <Select
              loading={dimensionsLoading}
              options={dimensionOptions}
              placeholder="选择虚拟分类"
              notFoundContent={dimensionsLoading ? '加载中…' : '暂无虚拟分类'}
            />
          </Form.Item>
          <Form.Item name="schedule_type" label="定时触发">
            <Select
              options={[
                { value: 'none', label: '不定时' },
                { value: 'daily', label: '每天' },
                { value: 'weekly', label: '每周' },
                { value: 'interval', label: '按间隔' },
              ]}
            />
          </Form.Item>
          {scheduleType === 'daily' || scheduleType === 'weekly' ? (
            <Form.Item name="schedule_time" label="执行时间">
              <Input type="time" />
            </Form.Item>
          ) : null}
          {scheduleType === 'weekly' ? (
            <Form.Item name="schedule_weekday" label="星期">
              <Select options={WEEKDAYS} />
            </Form.Item>
          ) : null}
          {scheduleType === 'interval' ? (
            <Form.Item name="schedule_hours" label="间隔（小时）">
              <Select
                options={[1, 2, 4, 6, 12, 24].map((h) => ({ value: h, label: `${h} 小时` }))}
              />
            </Form.Item>
          ) : null}
          <Form.Item name="is_enabled" label="启用任务" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </AppFormModal>
    </div>
  )
}
